import InfoIcon from '@pelckmans/business-components/icons/Info';
import Button from '@pelckmans/elementis/components/button';
import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import ButtonGroup from '@pelckmans/elementis/components/button-group';
import DropdownMenu from '@pelckmans/elementis/components/dropdown-menu';
import List from '@pelckmans/elementis/components/list';
import Pill from '@pelckmans/elementis/components/pill';
import { useToast } from '@pelckmans/elementis/components/toast/context/toast-context';
import Tooltip from '@pelckmans/elementis/components/tooltip';
import EditIcon from '@pelckmans/elementis/icons/edit';
import FileIcon from '@pelckmans/elementis/icons/file';
import UploadIcon from '@pelckmans/elementis/icons/upload';
import UsersIcon from '@pelckmans/elementis/icons/users';
import PlusIcon from '@pelckmans/elementis/icons/plus';

import { useCallback, useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { fetchMembers, getVOExportFile, Group, Member, removeMemberFromGroup } from '../../../../../../api/groups';
import { TOAST_ID } from '../../../../../../constants/ToastIds';
import ModalContext from '../../../../../../contexts/ModalContext';
import { useDefaultToast } from '../../../../../../hooks/useDefaultToast';
import useThrowAsyncError from '../../../../../../hooks/useThrowAsyncError';
import ActionMenuButton from '../../../../../../shared-components/ActionMenuButton';
import ConfirmationModal from '../../../../../../shared-components/ConfirmationModal';
import { downloadFile } from '../../../../../../utils/downloadFile';
import { buildGroupProps } from '../../../../../../utils/elementisUtils';
import { SchoolYear } from '../../../../hooks/useGroups';
import ImportMembersModal from '../../import-members/ImportMembersModal';
import MemberModal from '../../member/MemberModal';
import { FormValues } from '../EditGroupModal';
import useIsMobile from '../../../../../../hooks/useIsMobile';

const TRANSLATION_KEY = 'PAGES.MY_GROUPS.MODALS.EDIT_GROUP.MEMBERS';
const ADD_MEMBER_MODAL_ID = 'add-member-modal';
const EDIT_MEMBER_MODAL_ID = 'edit-member-modal';
const IMPORT_MEMBERS_MODAL_ID = 'import-members-modal';

interface Props {
  group: Group;
  selectedSchoolYear: SchoolYear;
  onMembersChange: (totalAccepted: number) => void;
}

export default function EditGroupMembers({ group, selectedSchoolYear, onMembersChange }: Props) {
  const { t } = useTranslation();
  const { showModal } = useContext(ModalContext);
  const handleError = useThrowAsyncError();
  const { addToast } = useToast();
  const { setValue } = useFormContext<FormValues>();
  const [isExporting, setIsExporting] = useState(false);
  const { failedToast, successToast } = useDefaultToast();
  const [members, setMembers] = useState<Member[]>([]);
  const isMobile = useIsMobile();

  const fetchGroupMembers = useCallback(async () => {
    try {
      const _members = await fetchMembers(selectedSchoolYear, group.id);
      setMembers(_members);
      setValue('totalAccepted', _members.length, { shouldValidate: true });
      onMembersChange(_members.length);
    } catch (e) {
      handleError(e);
    }
  }, [selectedSchoolYear, group.id, setValue, handleError, onMembersChange]);

  useEffect(() => {
    fetchGroupMembers();
  }, [fetchGroupMembers]);

  const refetchCallback = useCallback(() => {
    successToast();
    fetchGroupMembers();
  }, [successToast, fetchGroupMembers]);

  function handleAddMember() {
    showModal(<MemberModal id={ADD_MEMBER_MODAL_ID} groupId={group.id} members={members} refetch={refetchCallback} />);
  }

  function handleEditMember(currentMember: Member) {
    showModal(
      <MemberModal
        id={EDIT_MEMBER_MODAL_ID}
        groupId={group.id}
        members={members}
        refetch={refetchCallback}
        currentMember={currentMember}
      />,
    );
  }

  function handleImportMembers() {
    const existingUsernames = members.map(member => member.username!);
    showModal(
      <ImportMembersModal
        id={IMPORT_MEMBERS_MODAL_ID}
        group={group}
        refetch={refetchCallback}
        existingUsernames={existingUsernames}
      />,
    );
  }

  const handleCopy = (member: Member) => {
    const { username, password } = member;
    const { code } = group;

    navigator.clipboard.writeText(`
${t(`${TRANSLATION_KEY}.ACTIONS.MENU.ACTIONS.COPY_LOGIN_DETAILS.COURSE_CODE`, { code })}
${t(`${TRANSLATION_KEY}.ACTIONS.MENU.ACTIONS.COPY_LOGIN_DETAILS.USERNAME`, { username })}
${t(`${TRANSLATION_KEY}.ACTIONS.MENU.ACTIONS.COPY_LOGIN_DETAILS.PASSWORD`, { password })}
    `);

    addToast({
      id: TOAST_ID.COPIED_LOGIN_DETAILS,
      variant: 'success',
      title: t('TOAST.SUCCESS.TITLE'),
      children: t(`${TRANSLATION_KEY}.ACTIONS.MENU.ACTIONS.COPY_LOGIN_DETAILS.TOAST`),
    });
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const blob = await getVOExportFile(group.schoolYear, group.id);
      downloadFile(blob, 'export.xlsx');
      setIsExporting(false);
    } catch (err) {
      handleError(err);
    }
  };

  const handleDelete = async (member: Member) => {
    try {
      await removeMemberFromGroup(`${selectedSchoolYear.startYear}-${selectedSchoolYear.endYear}`, group.id, member.id);
      const newMembers = members.length - 1;
      setValue('totalAccepted', newMembers, { shouldValidate: true });
      setMembers(prev => prev.filter(m => m.id !== member.id));
      onMembersChange(newMembers);
      successToast();
    } catch (e) {
      failedToast();
    }
  };

  const onDeleteClick = async (member: Member) => {
    if (group.status !== 'published') {
      handleDelete(member);
    } else {
      showModal(
        <ConfirmationModal
          id="delete-member-confirmation"
          headerTitle={t(`PAGES.MY_GROUPS.MODALS.DELETE_MEMBER_CONFIRMATION.HEADER.TITLE`)}
          message={t(`PAGES.MY_GROUPS.MODALS.DELETE_MEMBER_CONFIRMATION.BODY`)}
          confirmationText={t(`PAGES.MY_GROUPS.MODALS.DELETE_MEMBER_CONFIRMATION.FOOTER.ACTIONS.CONFIRM`)}
          onConfirm={() => handleDelete(member)}
        />,
      );
    }
  };

  return (
    <List emptyMessage={t(`${TRANSLATION_KEY}.EMPTY_STATE`)} showSeparators>
      <List.Header
        buttonGroupProps={buildGroupProps(
          <>
            {members.length > 0 && (
              <Button
                aria-label={t(`${TRANSLATION_KEY}.ACTIONS.EXPORT.A11Y_LABEL`)}
                key="export"
                type="button"
                startIcon={<UploadIcon />}
                prominence="neutral"
                onClick={handleExport}
                busy={isExporting}
              >
                {!isMobile && t(`${TRANSLATION_KEY}.ACTIONS.EXPORT.LABEL`)}
              </Button>
            )}
            <ButtonDropdown
              key="add-member-dropdown"
              buttonProps={{
                prominence: 'secondary',
                ...(isMobile && {
                  startIcon: <PlusIcon data-testid="add-member-button" />,
                  'aria-label': t(`${TRANSLATION_KEY}.ACTIONS.ADD_MEMBER.BUTTON.A11Y_LABEL`),
                }),
              }}
              {...(!isMobile && { title: t(`${TRANSLATION_KEY}.ACTIONS.ADD_MEMBER.BUTTON.LABEL`) })}
              size="sm"
              disableBackgroundScroll
              flip
            >
              <ButtonDropdown.Item
                key="add-member"
                text={t(`${TRANSLATION_KEY}.ACTIONS.ADD_MEMBER.ADD_SINGLE_MEMBER`)}
                textIcon={<UsersIcon />}
                onClick={handleAddMember}
              />
              <ButtonDropdown.Item
                key="import-members"
                text={t(`${TRANSLATION_KEY}.ACTIONS.ADD_MEMBER.IMPORT_MEMBERS`)}
                textIcon={<FileIcon />}
                onClick={handleImportMembers}
              />
            </ButtonDropdown>
          </>,
        )}
        title={t(`${TRANSLATION_KEY}.TITLE`)}
        titleTag="h4"
        pill={
          <Pill icon={<UsersIcon data-testid="users-icon" />} prominence="success">
            {members.length}
          </Pill>
        }
        tooltip={
          <Tooltip anchor={<InfoIcon data-testid="tooltip-members" />} truncation="wrapped">
            <p>{t(`${TRANSLATION_KEY}.TOOLTIP`)}</p>
          </Tooltip>
        }
      />
      {members.map(member => {
        return (
          <List.Item
            key={member.id}
            title={`${member.lastName} ${member.firstName}`}
            subText={member.username}
            actionArea={
              <ButtonGroup aria-label={undefined} role="group" size="sm">
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => handleEditMember(member)}
                  aria-label={t(`${TRANSLATION_KEY}.ACTIONS.EDIT.A11Y_LABEL`)}
                  prominence="neutral"
                  key={`edit-member-${member.id}`}
                />
                <ActionMenuButton
                  key={`action-menu-button-${member.id}`}
                  buttonProps={{
                    ariaLabel: t(`${TRANSLATION_KEY}.ACTIONS.MENU.BUTTON.A11Y_LABEL`, {
                      memberName: `${member.lastName} ${member.firstName}`,
                    }),
                  }}
                  menuProps={{ ariaLabel: t(`${TRANSLATION_KEY}.ACTIONS.MENU.A11Y_LABEL`) }}
                >
                  {handleToggle => (
                    <>
                      <DropdownMenu.Item
                        key={`delete-member-${member.id}`}
                        text={t(`${TRANSLATION_KEY}.ACTIONS.MENU.ACTIONS.DELETE_MEMBER`, {
                          memberName: `${member.lastName} ${member.firstName}`,
                        })}
                        as="button"
                        role="menuitem"
                        onClick={() => onDeleteClick(member)}
                      />
                      <DropdownMenu.Item
                        key={`copy-login-details-${member.id}`}
                        text={t(`${TRANSLATION_KEY}.ACTIONS.MENU.ACTIONS.COPY_LOGIN_DETAILS.LABEL`)}
                        as="button"
                        role="menuitem"
                        onClick={() => {
                          handleCopy(member);
                          handleToggle();
                        }}
                      />
                    </>
                  )}
                </ActionMenuButton>
              </ButtonGroup>
            }
          />
        );
      })}
    </List>
  );
}
